import { useQuery, useQueryClient } from '@tanstack/react-query';
import useSlingApi from '@sdk/api/useSlingApi';
import { GetConversationPayload } from '@sdk/messages/types/apiTypes';

import { CONVERSATION_CACHE_TIME, CONVERSATION_STALE_TIME } from '@web/conversations/consts/cache';
import { CONVERSATION_PLACEHOLDER } from '@web/conversations/consts/conversations';
import {
  getConversationQueryKey,
  getConversationsQueryKey,
} from '@web/conversations/utils/getQueryKey';
import { ConversationBasic } from '@web/conversations/types/Conversation';

import { useConversationMappers } from './useConversationMappers';

type Props = GetConversationPayload;

export const useGetConversation = ({ id }: Props) => {
  const { conversation } = useSlingApi();
  const client = useQueryClient();
  const { isReady, mapAPIConversationToUI } = useConversationMappers();
  const queryKey = getConversationQueryKey(id);

  const result = useQuery({
    queryKey,
    queryFn: async () => {
      const { data } = await conversation.get({ id });

      return mapAPIConversationToUI(data);
    },

    cacheTime: CONVERSATION_CACHE_TIME,
    enabled: isReady,
    placeholderData: () => {
      const placeholder = {
        ...CONVERSATION_PLACEHOLDER,
        id,
      };

      const key = getConversationsQueryKey();
      const cachedConversations = client.getQueryData<ConversationBasic[]>(key);
      const conversation =
        cachedConversations && cachedConversations.find((conversation) => conversation.id === id);

      if (conversation) {
        return {
          ...placeholder,
          ...conversation,
        };
      }

      return placeholder;
    },
    staleTime: CONVERSATION_STALE_TIME,
  });

  return result;
};
